import * as React from 'react';
import {
  MiddlewareState,
  safePolygon,
  useClick,
  useDismiss,
  useFloatingParentNodeId,
  useHover,
  useInteractions,
  useListNavigation,
  useRole,
  type UseDismissProps,
  type UseFloatingReturn,
  type UseListNavigationProps,
} from '@floating-ui/react';

import {useFloatingBase} from '../../hooks';
import type {UseFloatingBaseProps} from '../../hooks';
import {SPACING_BASE_VALUE} from '../../hooks/useFloating/useFloatingBase';
import {useMenuContentContext} from './useMenuContent';
import type {MenuProps} from './_types';

export type MenuContextType = {
  activeIndex: UseListNavigationProps['activeIndex'];
  ariaLabel?: string;
  ariaLabelledBy?: string;
  context: UseFloatingReturn['context'];
  elementsRef: UseListNavigationProps['listRef'];
  floatingStyles: UseFloatingReturn['floatingStyles'];
  getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'];
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
  getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps'];
  height?: MenuProps['height'];
  isNested: boolean;
  maskProps?: MenuProps['maskProps'];
  minHeight?: MenuProps['minHeight'];
  nodeId: string | undefined;
  parent: ReturnType<typeof useMenuContentContext>;
  parentId: ReturnType<typeof useFloatingParentNodeId>;
  refs: UseFloatingReturn['refs'];
  setActiveIndex: UseListNavigationProps['onNavigate'];
  setIsTriggerDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  shouldAllowPointerEvents: boolean;
  shouldPreventBodyScroll: boolean;
  shouldResizeMenuToMatchTrigger: boolean;
  shouldShowMask: boolean;
  shouldShowSelectedCheck: boolean;
  triggerRef: React.ForwardedRef<HTMLElement>;
  shouldTruncateText: boolean;
  verticalItemAlignment: MenuProps['verticalItemAlignment'];
};

export const MenuContext = React.createContext<MenuContextType | null>(null);
export const useMenuContext = (): MenuContextType => {
  const context = React.useContext(MenuContext);
  const isTestEnvironment = process.env.NODE_ENV === 'test';

  if (!context && !isTestEnvironment) {
    throw new Error('You must use component within Menu Context');
  }

  /**
   * Context should only be null in a test environment.
   * We're okay with this because we want to be able to test Menu's subcomponents in isolation.
   */
  // @ts-expect-error TS2322 "null is not assignable to type MenuContextType"
  return context;
};

type UseMenuProps = UseFloatingBaseProps & {
  activeIndex: UseListNavigationProps['activeIndex'];
  defaultIsOpen?: boolean;
  elementsRef: UseListNavigationProps['listRef'];
  gutter?: number;
  isNested?: UseListNavigationProps['nested'];
  maxHeight?: MenuProps['maxHeight'];
  setActiveIndex: UseListNavigationProps['onNavigate'];
  shouldHideOnClickOutside?: UseDismissProps['outsidePress'];
  shouldLoop?: UseListNavigationProps['loop'];
  shouldResizeMenuToMatchTrigger?: boolean;
};

export const useMenu = ({
  activeIndex,
  containerPadding,
  defaultIsOpen = false,
  elementsRef,
  gutter,
  isDisabled,
  isNested,
  isOpen,
  maxHeight,
  maxWidth,
  minWidth,
  onOpenChange,
  placement: placementProp,
  setActiveIndex,
  shouldEscapeViewport,
  shouldHideOnClickOutside,
  shouldLoop,
  shouldResizeMenuToMatchTrigger,
}: UseMenuProps) => {
  const placement =
    placementProp ?? (isNested ? 'right-start' : 'bottom-start');

  const offsetModifier: UseFloatingBaseProps['offsetModifier'] =
    React.useCallback(
      (options: MiddlewareState) => {
        if (isNested) {
          /**
           * If the Menu is nested, we'll base the vertical offset value on the Menu's paddingTop.
           */
          const floatingElement = options.elements.floating;
          const floatingElementPadding =
            window.getComputedStyle(floatingElement).paddingTop;
          return {
            verticalOffset: -parseInt(floatingElementPadding, 10),
            horizontalOffset: 0,
          };
        } else {
          if (gutter === null || gutter === undefined) {
            return null;
          }

          /**
           * If a gutter value is provided, and the Menu is not nested,
           * we map the gutter value to the appropriate horizontal or vertical offset prop.
           */
          const gutterSpacingValue = gutter * SPACING_BASE_VALUE;
          if (placement.includes('top') || placement.includes('bottom')) {
            return {verticalOffset: gutterSpacingValue};
          } else {
            return {horizontalOffset: gutterSpacingValue};
          }
        }
      },
      [isNested, gutter, placement]
    );

  const data = useFloatingBase({
    containerPadding,
    defaultIsOpen,
    isDisabled,
    isOpen,
    maxHeight,
    maxWidth,
    minWidth,
    offsetModifier,
    onOpenChange,
    placement,
    shouldMatchTriggerWidth: shouldResizeMenuToMatchTrigger,
    shouldEscapeViewport,
  });

  const {context, onOutsidePress} = data;

  const hover = useHover(context, {
    enabled: isNested,
    delay: {open: 75},
    handleClose: safePolygon({
      blockPointerEvents: true,
      buffer: 1,
      requireIntent: false,
    }),
  });
  const click = useClick(context, {
    event: 'mousedown',
    toggle: !isNested,
    ignoreMouse: isNested,
  });
  const role = useRole(context);
  const dismiss = useDismiss(context, {
    bubbles: false,
    outsidePress: shouldHideOnClickOutside ? onOutsidePress : false,
  });
  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    nested: isNested,
    onNavigate: setActiveIndex,
    loop: shouldLoop,
    /**
     * disabledIndices is deliberately an empty array.
     * This is to override useListNavigation's default behavior
     * of skipping indices that have disabled or aria-disabled attributes.
     */
    disabledIndices: [],
  });

  const interactions = useInteractions([
    hover,
    click,
    role,
    dismiss,
    listNavigation,
  ]);

  return React.useMemo(
    () => ({
      ...interactions,
      ...data,
    }),
    [data, interactions]
  );
};
